import React, { useState, useEffect, useCallback, useLayoutEffect, useRef, useMemo } from 'react';
import { Button, Spinner } from 'react-bootstrap';
import { useLocation } from 'react-router-dom';
import { useAuth } from '../../contexts/AuthContext';
import { getTrainingContextFromPathname } from '../../constants/constants';
import { getWebsiteTrainingTourConfig } from '../../constants/websiteTrainingTourSteps';
import api from '../../utils/api';
import logger from '../../utils/logger';
import {
  WEBSITE_TRAINING_REFETCH_EVENT,
  WEBSITE_TRAINING_START_EVENT
} from '../../utils/websiteTrainingTourEvents';
import {
  fetchWebsiteTrainingPending,
  clearWebsiteTrainingPendingCache
} from '../../utils/websiteTrainingPendingApi';
import {
  findTourStepTarget,
  activateTabForStep,
  getHighlightRect,
  getTooltipPosition
} from './websiteTrainingTourUtils';
import './WebsiteTrainingTour.css';

const TOUR_START_DELAY_MS = 600;
const TAB_CONTENT_DELAY_MS = 400;

const WebsiteTrainingTour = () => {
  const { currentUser } = useAuth();
  const location = useLocation();
  const [active, setActive] = useState(false);
  const [loading, setLoading] = useState(false);
  const [acknowledging, setAcknowledging] = useState(false);
  const [pendingData, setPendingData] = useState(null);
  const [stepIndex, setStepIndex] = useState(0);
  const [highlightRect, setHighlightRect] = useState(null);
  const [tooltipStyle, setTooltipStyle] = useState({});
  const [missingTab, setMissingTab] = useState(false);
  const [reviewMode, setReviewMode] = useState(false);
  const [previewMode, setPreviewMode] = useState(false);
  const activateTimerRef = useRef(null);

  const trainingContext = useMemo(
    () => getTrainingContextFromPathname(location.pathname),
    [location.pathname]
  );
  const tourConfig = trainingContext?.location
    ? getWebsiteTrainingTourConfig(trainingContext.location)
    : null;
  const tourSteps = tourConfig?.steps || [];
  const totalSteps = 1 + tourSteps.length;
  const isIntroStep = stepIndex === 0;
  const featureStep = !isIntroStep ? tourSteps[stepIndex - 1] : null;
  const isLastStep = stepIndex === totalSteps - 1;

  const clearActivateTimer = useCallback(() => {
    if (activateTimerRef.current) {
      window.clearTimeout(activateTimerRef.current);
      activateTimerRef.current = null;
    }
  }, []);

  const startTour = useCallback((data, { immediate = false } = {}) => {
    if (!data?.assignment || !data?.training) {
      setPendingData(null);
      setActive(false);
      setReviewMode(false);
      setPreviewMode(false);
      return false;
    }

    setPendingData(data);
    setReviewMode(Boolean(data.reviewMode));
    setPreviewMode(Boolean(data.previewMode));
    setStepIndex(0);
    clearActivateTimer();

    if (immediate) {
      setActive(true);
      return true;
    }

    const delay =
      trainingContext?.kind === 'client-profile' ? 800 : TOUR_START_DELAY_MS;
    activateTimerRef.current = window.setTimeout(() => setActive(true), delay);
    return true;
  }, [clearActivateTimer, trainingContext?.kind]);

  const fetchPending = useCallback(async ({ silent = false, force = false } = {}) => {
    if (!currentUser || !trainingContext?.location || !tourConfig) {
      setPendingData(null);
      setActive(false);
      setReviewMode(false);
      setPreviewMode(false);
      return null;
    }

    if (!silent) {
      setLoading(true);
    }
    try {
      const data = await fetchWebsiteTrainingPending(trainingContext.location, { force });
      if (data?.assignment && data?.training) {
        startTour(data, { immediate: silent });
        return data;
      }
      setPendingData(null);
      setActive(false);
      setReviewMode(false);
      setPreviewMode(false);
      return null;
    } catch (err) {
      setPendingData(null);
      setActive(false);
      setReviewMode(false);
      setPreviewMode(false);
      return null;
    } finally {
      if (!silent) {
        setLoading(false);
      }
    }
  }, [currentUser, trainingContext, tourConfig, startTour]);

  const fetchPendingRef = useRef(fetchPending);
  fetchPendingRef.current = fetchPending;

  const startTourRef = useRef(startTour);
  startTourRef.current = startTour;

  useEffect(() => {
    fetchPendingRef.current();
  }, [trainingContext?.location, currentUser?.uid, tourConfig]);

  useEffect(() => {
    const onRefetch = () => {
      fetchPendingRef.current({ silent: true });
    };
    const onStart = (event) => {
      if (event.detail?.assignment && event.detail?.training) {
        startTourRef.current(event.detail, { immediate: true });
      }
    };

    window.addEventListener(WEBSITE_TRAINING_REFETCH_EVENT, onRefetch);
    window.addEventListener(WEBSITE_TRAINING_START_EVENT, onStart);
    return () => {
      window.removeEventListener(WEBSITE_TRAINING_REFETCH_EVENT, onRefetch);
      window.removeEventListener(WEBSITE_TRAINING_START_EVENT, onStart);
      clearActivateTimer();
    };
  }, [clearActivateTimer]);

  useEffect(() => {
    if (!active) {
      document.body.classList.remove('website-training-tour-active');
      document.body.classList.remove('website-training-tour-scrollable');
      return undefined;
    }
    if (trainingContext?.kind === 'client-profile') {
      document.body.classList.add('website-training-tour-scrollable');
    } else {
      document.body.classList.add('website-training-tour-active');
    }
    return () => {
      document.body.classList.remove('website-training-tour-active');
      document.body.classList.remove('website-training-tour-scrollable');
    };
  }, [active, trainingContext?.kind]);

  const updateHighlight = useCallback(() => {
    if (!active || isIntroStep || !featureStep) {
      setHighlightRect(null);
      setTooltipStyle(getTooltipPosition(null));
      setMissingTab(false);
      return undefined;
    }

    const scrollBlock = trainingContext?.kind === 'client-profile' ? 'center' : 'nearest';

    const applyHighlight = (targetEl) => {
      if (!targetEl) {
        setHighlightRect(null);
        setTooltipStyle(getTooltipPosition(null));
        setMissingTab(true);
        return;
      }

      setMissingTab(false);
      targetEl.scrollIntoView({ block: scrollBlock, inline: 'nearest', behavior: 'smooth' });
      requestAnimationFrame(() => {
        const rect = getHighlightRect(targetEl);
        setHighlightRect(rect);
        const tooltipWidth = trainingContext?.kind === 'client-profile' ? 420 : 360;
        const tooltipHeight = trainingContext?.kind === 'client-profile' ? 300 : 200;
        setTooltipStyle(getTooltipPosition(rect, tooltipWidth, tooltipHeight));
      });
    };

    const resolveTarget = () => {
      if (featureStep.tabLabel && (featureStep.selector || featureStep.buttonLabel)) {
        activateTabForStep(featureStep);
        return findTourStepTarget(featureStep);
      }
      const targetEl = findTourStepTarget(featureStep);
      if (featureStep.tabLabel && !featureStep.selector && !featureStep.buttonLabel && targetEl) {
        activateTabForStep(featureStep);
      }
      return targetEl;
    };

    if (featureStep.tabLabel && (featureStep.selector || featureStep.buttonLabel)) {
      activateTabForStep(featureStep);
      const timer = window.setTimeout(() => {
        applyHighlight(findTourStepTarget(featureStep));
      }, TAB_CONTENT_DELAY_MS);
      return () => window.clearTimeout(timer);
    }

    applyHighlight(resolveTarget());
    return undefined;
  }, [active, isIntroStep, featureStep, trainingContext?.kind]);

  useLayoutEffect(() => {
    return updateHighlight();
  }, [updateHighlight, stepIndex]);

  useEffect(() => {
    if (!active || isIntroStep) {
      return undefined;
    }
    const onReflow = () => updateHighlight();
    window.addEventListener('resize', onReflow);
    window.addEventListener('scroll', onReflow, true);
    return () => {
      window.removeEventListener('resize', onReflow);
      window.removeEventListener('scroll', onReflow, true);
    };
  }, [active, isIntroStep, updateHighlight]);

  const handleComplete = async () => {
    if (reviewMode || previewMode || !pendingData?.assignment?.id) {
      setActive(false);
      setPendingData(null);
      setReviewMode(false);
      setPreviewMode(false);
      return;
    }
    setAcknowledging(true);
    try {
      await api.patch(`/api/trainings/assignments/${pendingData.assignment.id}`, {
        status: 'completed'
      });
      clearWebsiteTrainingPendingCache();
      setActive(false);
      setPendingData(null);
    } catch (err) {
      logger.error('Error completing website training tour:', err);
    } finally {
      setAcknowledging(false);
    }
  };

  const handleNext = () => {
    if (isLastStep) {
      handleComplete();
      return;
    }
    setStepIndex((i) => i + 1);
  };

  const handleBack = () => {
    if (stepIndex > 0) {
      setStepIndex((i) => i - 1);
    }
  };

  if (!trainingContext || !tourConfig) {
    return null;
  }

  if (loading && !active) {
    return null;
  }

  if (!pendingData || !active) {
    return null;
  }

  const trainingTitle = pendingData.training?.title || `${trainingContext.location} Training`;
  const stepTitle = isIntroStep ? trainingTitle : featureStep?.title;
  const stepBody = isIntroStep ? tourConfig.intro : featureStep?.body;
  const stepBullets = isIntroStep ? tourConfig.introBullets : featureStep?.bullets;
  const isClientProfileTour = trainingContext?.kind === 'client-profile';

  return (
    <div
      className={`website-training-tour${isClientProfileTour ? ' website-training-tour--client-profile' : ''}`}
      role="dialog"
      aria-modal="true"
      aria-labelledby="website-training-tour-title"
    >
      {(isIntroStep || missingTab || !highlightRect) &&
        <div className="website-training-tour__backdrop" aria-hidden="true" />
      }

      {highlightRect &&
        <div
          className={`website-training-tour__spotlight${isClientProfileTour ? ' website-training-tour__spotlight--pulse' : ''}`}
          style={{
            top: highlightRect.top,
            left: highlightRect.left,
            width: highlightRect.width,
            height: highlightRect.height
          }}
        />
      }

      <div
        className={`website-training-tour__card ${isIntroStep ? 'website-training-tour__card--intro' : ''}`}
        style={isIntroStep ? undefined : tooltipStyle}
      >
        <div className="website-training-tour__progress">
          Step {stepIndex + 1} of {totalSteps}
        </div>
        <h2 id="website-training-tour-title" className="website-training-tour__title">
          {stepTitle}
        </h2>
        {stepBody &&
          <>
            {!isIntroStep &&
              <p className="website-training-tour__kicker">What this does</p>
            }
            <p className="website-training-tour__body">{stepBody}</p>
          </>
        }
        {!isIntroStep && featureStep?.tip &&
          <>
            <p className="website-training-tour__kicker">When to use it</p>
            <p className="website-training-tour__tip">{featureStep.tip}</p>
          </>
        }
        {Array.isArray(stepBullets) && stepBullets.length > 0 &&
          <ul className="website-training-tour__bullets">
            {stepBullets.map((item) =>
              <li key={item}>{item}</li>
            )}
          </ul>
        }
        {missingTab &&
          <p className="website-training-tour__missing text-muted small">
            This section is not visible for your role. Continue to the next step.
          </p>
        }
        {previewMode &&
          <p className="website-training-tour__missing text-muted small">
            Preview mode — assign this training to staff to track completion.
          </p>
        }
        {reviewMode && !previewMode &&
          <p className="website-training-tour__missing text-muted small">
            Review mode — you already completed this training.
          </p>
        }
        <div className="website-training-tour__actions">
          {stepIndex > 0 &&
            <Button variant="outline-secondary" onClick={handleBack} disabled={acknowledging}>
              Back
            </Button>
          }
          <Button variant="primary" onClick={handleNext} disabled={acknowledging} className="ms-auto">
            {acknowledging ?
              <>
                <Spinner size="sm" className="me-2" />
                Saving…
              </> :
              isLastStep ?
                (reviewMode || previewMode ? 'Close' : 'Finish') :
                'Next'
            }
          </Button>
        </div>
      </div>
    </div>
  );
};

export default WebsiteTrainingTour;
